// Product detail gallery — main viewer + thumbnail strip (images + videos).
import React, { useEffect, useState, useMemo } from "react";
import { Play } from "lucide-react";
import { resolveImage } from "./ProductCard";

const ProductGallery = ({ images = [], videos = [], productName = "" }) => {
    const items = useMemo(() => {
        const imgItems = (images || []).map((img, i) => ({
            kind: "image",
            id: img.id || `img-${i}`,
            url: resolveImage(img),
            poster: resolveImage(img),
        }));
        const vidItems = (videos || []).map((v, i) => {
            const vidUrl = v.url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${v.url}` : v.url;
            const posterUrl = v.thumbnail_url
                ? (v.thumbnail_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${v.thumbnail_url}` : v.thumbnail_url)
                : "/pills-logo.jpg";
            return { kind: "video", id: v.id || `vid-${i}`, url: vidUrl, poster: posterUrl };
        });
        return [...imgItems, ...vidItems];
    }, [images, videos]);

    const [activeIdx, setActiveIdx] = useState(0);
    useEffect(() => { setActiveIdx(0); }, [items.length]);

    if (items.length === 0) {
        return (
            <div className="aspect-square w-full overflow-hidden rounded-2xl border border-zinc-100 bg-zinc-50" data-testid="product-gallery-empty">
                <img src="/pills-logo.jpg" alt={productName} className="h-full w-full object-contain p-8" />
            </div>
        );
    }

    const active = items[Math.min(activeIdx, items.length - 1)];

    return (
        <div className="space-y-3" data-testid="product-gallery">
            <div className="aspect-square w-full overflow-hidden rounded-2xl border border-zinc-100 bg-zinc-50" data-testid="product-gallery-main">
                {active.kind === "image" ? (
                    <img
                        src={active.url}
                        alt={productName}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.src = "/pills-logo.jpg"; }}
                        className="h-full w-full object-contain"
                    />
                ) : (
                    <video
                        key={active.id}
                        src={active.url}
                        poster={active.poster}
                        controls
                        playsInline
                        className="h-full w-full bg-black object-contain"
                        data-testid="product-gallery-video"
                    />
                )}
            </div>
            {items.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-1" data-testid="product-gallery-thumbs">
                    {items.map((it, i) => (
                        <button
                            key={it.id}
                            type="button"
                            onClick={() => setActiveIdx(i)}
                            className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 transition-colors ${i === activeIdx ? "border-brand-500" : "border-transparent hover:border-brand-200"}`}
                            data-testid={`product-gallery-thumb-${i}`}
                        >
                            <img src={it.poster} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.src = "/pills-logo.jpg"; }} />
                            {it.kind === "video" && (
                                <span className="absolute inset-0 grid place-items-center bg-black/30">
                                    <Play className="h-4 w-4 fill-white text-white" />
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProductGallery;
